import api from './api';

export async function generateScript(payload) {
  return api.post('/api/content-ai/scripts', payload);
}

export async function generateYoutubeMetadata(payload) {
  return api.post('/api/content-ai/youtube-metadata', payload);
}

export async function generateImage(payload) {
  return api.post('/api/content-ai/images', payload);
}

export async function getAiSettings() {
  return api.get('/api/ai/settings');
}

export async function updateAiSettings(payload) {
  return api.put('/api/ai/settings', payload || {});
}

export async function listVideoTemplates() {
  return api.get('/api/video-templates');
}

export async function createVideoTemplate(payload) {
  return api.post('/api/video-templates', payload);
}

export async function updateVideoTemplate(id, payload) {
  return api.put(`/api/video-templates/${id}`, payload);
}

export async function listVideos(limit = 50) {
  return api.get(`/api/videos?limit=${encodeURIComponent(String(limit))}`);
}

export async function getVideo(id) {
  return api.get(`/api/videos/${id}`);
}

export async function createVideo(payload) {
  return api.post('/api/videos', payload);
}

export async function getVideoTimeline(id) {
  return api.get(`/api/videos/${id}/timeline`);
}

export async function updateVideoTimeline(id, payload) {
  return api.put(`/api/videos/${id}/timeline`, payload || {});
}

export async function listVideoRenders(id) {
  return api.get(`/api/videos/${id}/renders`);
}

export async function enqueueProVideoRender(id, payload) {
  return api.post(`/api/videos/${id}/render`, payload || {});
}

export async function listMediaAssets(kind, limit = 50, options = {}) {
  const qs = new URLSearchParams();
  if (kind) qs.set('kind', kind);
  qs.set('limit', String(limit));
  if (options?.youtubeChannelId != null) qs.set('youtubeChannelId', String(options.youtubeChannelId));
  if (options?.includeShared != null) qs.set('includeShared', options.includeShared ? 'true' : 'false');
  return api.get(`/api/media-assets?${qs.toString()}`);
}

export async function uploadMediaAsset({ file, kind, youtubeChannelId, fileName, onProgress }) {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', String(kind || ''));
  if (youtubeChannelId != null) form.append('youtubeChannelId', String(youtubeChannelId));
  if (fileName) form.append('fileName', String(fileName));
  return api.upload('/api/media-assets/upload', form, { onProgress });
}

export async function mintPublicAssetLink(id, ttlMinutes) {
  return api.post(`/api/media-assets/${id}/public`, { ttlMinutes });
}

export async function listYoutubeChannels() {
  return api.get('/api/youtube/channels');
}

export async function createYoutubeChannel(payload) {
  return api.post('/api/youtube/channels', payload);
}

export async function updateYoutubeChannel(id, payload) {
  return api.put(`/api/youtube/channels/${id}`, payload || {});
}

export async function deleteYoutubeChannel(id) {
  return api.delete(`/api/youtube/channels/${id}`);
}

export async function startYoutubeOauth(channelId, redirectUri) {
  return api.post(`/api/youtube/channels/${channelId}/oauth/start`, { redirectUri });
}

export async function finishYoutubeOauth(payload) {
  return api.post('/api/youtube/oauth/finish', payload);
}

export async function listYoutubeUploads(limit = 50) {
  return api.get(`/api/youtube/uploads?limit=${encodeURIComponent(String(limit))}`);
}

export async function createYoutubeUpload(payload) {
  return api.post('/api/youtube/uploads', payload);
}

export async function enqueueYoutubeUpload(id) {
  return api.post(`/api/youtube/uploads/${id}/enqueue`, {});
}

export async function retryYoutubeUpload(id) {
  return api.post(`/api/youtube/uploads/${id}/retry`, {});
}

export async function listYoutubeAnalytics(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.limit != null) qs.set('limit', String(p.limit));
  if (p.kind) qs.set('kind', String(p.kind));
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.youtubeVideoId) qs.set('youtubeVideoId', String(p.youtubeVideoId));
  if (p.from) qs.set('from', String(p.from));
  if (p.to) qs.set('to', String(p.to));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube/analytics${suffix}`);
}

export async function collectYoutubeAnalytics() {
  return api.post('/api/youtube/analytics/collect', {});
}

export async function getYoutubeGrowthInsights(youtubeChannelId) {
  const qs = youtubeChannelId ? `?youtubeChannelId=${encodeURIComponent(String(youtubeChannelId))}` : '';
  return api.get(`/api/youtube/growth${qs}`);
}

export async function runYoutubeGrowthInsights(youtubeChannelId) {
  return api.post('/api/youtube/growth/run', { youtubeChannelId: Number(youtubeChannelId) });
}

export async function listYoutubeScriptTemplates() {
  return api.get('/api/content-ai/youtube-scripts/templates');
}

export async function listYoutubeScripts(limit = 50) {
  return api.get(`/api/content-ai/youtube-scripts?limit=${encodeURIComponent(String(limit))}`);
}

export async function getYoutubeScript(id) {
  return api.get(`/api/content-ai/youtube-scripts/${id}`);
}

export async function createYoutubeScript(payload) {
  return api.post('/api/content-ai/youtube-scripts', payload);
}

export async function updateYoutubeScript(id, payload) {
  return api.put(`/api/content-ai/youtube-scripts/${id}`, payload || {});
}

export async function deleteYoutubeScript(id) {
  return api.delete(`/api/content-ai/youtube-scripts/${id}`);
}

export async function generateYoutubeScript(id, payload) {
  return api.post(`/api/content-ai/youtube-scripts/${id}/generate`, payload || {});
}

export async function saveYoutubeScriptDraft(id, draftJson) {
  return api.put(`/api/content-ai/youtube-scripts/${id}/draft`, { draftJson });
}

export async function createYoutubeScriptVersionFromDraft(id) {
  return api.post(`/api/content-ai/youtube-scripts/${id}/versions`, {});
}

export async function regenerateYoutubeScriptSection(id, payload) {
  return api.post(`/api/content-ai/youtube-scripts/${id}/regenerate-section`, payload);
}

export async function restoreYoutubeScriptVersion(id, version) {
  return api.post(`/api/content-ai/youtube-scripts/${id}/versions/${version}/restore`, {});
}

export async function getVoiceSettings() {
  return api.get('/api/voice-ai/settings');
}

export async function updateVoiceSettings(payload) {
  return api.put('/api/voice-ai/settings', payload || {});
}

export async function listVoiceProfiles() {
  return api.get('/api/voice-ai/profiles');
}

export async function createVoiceProfile(payload) {
  return api.post('/api/voice-ai/profiles', payload || {});
}

export async function cloneXttsVoiceProfile(payload) {
  return api.post('/api/voice-ai/profiles/clone/xtts', payload || {});
}

export async function updateVoiceProfile(id, payload) {
  return api.put(`/api/voice-ai/profiles/${id}`, payload || {});
}

export async function testVoice(payload) {
  return api.post('/api/voice-ai/profiles/test', payload || {});
}

export async function enqueueNarration(payload) {
  return api.post('/api/voice-ai/narrations', payload || {});
}

export async function listNarrationJobs(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.limit != null) qs.set('limit', String(p.limit));
  if (p.status) qs.set('status', String(p.status));
  if (p.voiceProfileId != null) qs.set('voiceProfileId', String(p.voiceProfileId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/voice-ai/narrations${suffix}`);
}

export async function getNarrationJob(id) {
  return api.get(`/api/voice-ai/narrations/${id}`);
}

export async function retryNarrationJob(id) {
  return api.post(`/api/voice-ai/narrations/${id}/retry`, {});
}

export async function reprocessNarrationJob(id, payload) {
  return api.post(`/api/voice-ai/narrations/${id}/reprocess`, payload || {});
}

export async function getImageSettings() {
  return api.get('/api/image-ai/settings');
}

export async function updateImageSettings(payload) {
  return api.put('/api/image-ai/settings', payload || {});
}

export async function getStockMediaSettings() {
  return api.get('/api/image-ai/stock-settings');
}

export async function updateStockMediaSettings(payload) {
  return api.put('/api/image-ai/stock-settings', payload || {});
}

export async function listImageScenes(limit = 100) {
  return api.get(`/api/image-ai/scenes?limit=${encodeURIComponent(String(limit))}`);
}

export async function createImageScene(payload) {
  return api.post('/api/image-ai/scenes', payload || {});
}

export async function updateImageScene(id, payload) {
  return api.put(`/api/image-ai/scenes/${id}`, payload || {});
}

export async function previewImage(payload) {
  return api.post('/api/image-ai/preview', payload || {});
}

export async function enqueueImageSceneGenerate(id, payload) {
  return api.post(`/api/image-ai/scenes/${id}/generate`, payload || {});
}

export async function listImageJobs(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.limit != null) qs.set('limit', String(p.limit));
  if (p.status) qs.set('status', String(p.status));
  if (p.sceneId != null) qs.set('sceneId', String(p.sceneId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/image-ai/jobs${suffix}`);
}

export async function retryImageJob(id) {
  return api.post(`/api/image-ai/jobs/${id}/retry`, {});
}

export async function setSceneImageFavorite(sceneId, sceneImageId, isFavorite) {
  return api.post(`/api/image-ai/scenes/${sceneId}/images/${sceneImageId}/favorite`, { isFavorite: isFavorite === true });
}

export async function listYoutubeAutomationSettings(youtubeChannelId) {
  const qs = youtubeChannelId ? `?youtubeChannelId=${encodeURIComponent(String(youtubeChannelId))}` : '';
  return api.get(`/api/youtube-automation/settings${qs}`);
}

export async function updateYoutubeAutomationSettings(youtubeChannelId, payload) {
  return api.put(`/api/youtube-automation/settings/${youtubeChannelId}`, payload || {});
}

export async function listYoutubeTrends(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/trends${suffix}`);
}

export async function collectYoutubeTrends(payload) {
  return api.post('/api/youtube-automation/trends/collect', payload || {});
}

export async function listYoutubePlans(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.from) qs.set('from', String(p.from));
  if (p.to) qs.set('to', String(p.to));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/plans${suffix}`);
}

export async function deleteYoutubeFailedPlans(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.from) qs.set('from', String(p.from));
  if (p.to) qs.set('to', String(p.to));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.delete(`/api/youtube-automation/plans/failed${suffix}`);
}

export async function deleteYoutubePlan(id) {
  return api.delete(`/api/youtube-automation/plans/${encodeURIComponent(String(id))}`);
}

export async function generateYoutubePlans(payload) {
  return api.post('/api/youtube-automation/plans/generate', payload || {});
}

export async function getYoutubePlansGenerateStatus(jobId) {
  const qs = jobId ? `?jobId=${encodeURIComponent(String(jobId))}` : '';
  return api.get(`/api/youtube-automation/plans/generate/status${qs}`);
}

export async function listYoutubeTopicQueue(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.status) qs.set('status', String(p.status));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/topics${suffix}`);
}

export async function createYoutubeTopicQueue(payload) {
  return api.post('/api/youtube-automation/topics', payload || {});
}

export async function cancelYoutubeTopicQueueItem(id) {
  return api.post(`/api/youtube-automation/topics/${encodeURIComponent(String(id))}/cancel`, {});
}

export async function listYoutubeAutomationRuns(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/runs${suffix}`);
}

export async function triggerYoutubeDailyRun(payload) {
  return api.post('/api/youtube-automation/runs/daily', payload || {});
}

export async function triggerYoutubeAnalyticsAi(payload) {
  return api.post('/api/youtube-automation/runs/analytics-ai', payload || {});
}

export async function listYoutubeCampaigns(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/campaigns${suffix}`);
}

export async function getYoutubeCampaign(id) {
  return api.get(`/api/youtube-automation/campaigns/${encodeURIComponent(String(id))}`);
}

export async function generateYoutubeCampaign(payload) {
  return api.post('/api/youtube-automation/campaigns/generate', payload || {});
}

export async function enqueueYoutubeCampaign(id) {
  return api.post(`/api/youtube-automation/campaigns/${encodeURIComponent(String(id))}/enqueue`, {});
}

export async function listYoutubeShortsJobs(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.status) qs.set('status', String(p.status));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/shorts-jobs${suffix}`);
}

export async function listYoutubeRepurposeJobs(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.youtubeChannelId != null) qs.set('youtubeChannelId', String(p.youtubeChannelId));
  if (p.status) qs.set('status', String(p.status));
  if (p.platform) qs.set('platform', String(p.platform));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/youtube-automation/repurpose-jobs${suffix}`);
}

export async function upscaleSceneImage(sceneId, sceneImageId, scale) {
  return api.post(`/api/image-ai/scenes/${sceneId}/images/${sceneImageId}/upscale`, { scale: scale != null ? Number(scale) : 2 });
}

export async function listProductions(params) {
  const qs = new URLSearchParams();
  const p = params && typeof params === 'object' ? params : {};
  if (p.status) qs.set('status', String(p.status));
  if (p.limit != null) qs.set('limit', String(p.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions${suffix}`);
}

export async function createProduction(payload) {
  return api.post('/api/production-studio/productions', payload || {});
}

export async function getProductionTracking(id) {
  return api.get(`/api/production-studio/productions/${id}/tracking`);
}

export async function getProduction(id) {
  return api.get(`/api/production-studio/productions/${id}`);
}

export async function deleteProduction(id) {
  return api.delete(`/api/production-studio/productions/${id}`);
}

export async function getProductionContentIntelligence(id) {
  return api.get(`/api/production-studio/productions/${id}/content-intelligence`);
}

export async function runProductionContentIntelligence(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/content-intelligence/run`, payload || {});
}

export async function getProductionScriptEngine(id) {
  return api.get(`/api/production-studio/productions/${id}/script-engine`);
}

export async function runProductionScriptEngine(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/script-engine/run`, payload || {});
}

export async function updateProduction(id, payload) {
  return api.put(`/api/production-studio/productions/${id}`, payload || {});
}

export async function updateProductionRefinement(id, payload) {
  return api.put(`/api/production-studio/productions/${id}/refinement`, payload || {});
}

export async function replaceProductionMainVideo(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/main-video/replace`, payload || {});
}

export async function generateProductionSceneImage(id, sceneId, payload) {
  return api.post(`/api/production-studio/productions/${id}/scenes/${sceneId}/generate-image`, payload || {});
}

export async function generateProductionSceneVideo(id, sceneId, payload) {
  return api.post(`/api/production-studio/productions/${id}/scenes/${sceneId}/generate-video`, payload || {});
}

export async function runProduction(id) {
  return api.post(`/api/production-studio/productions/${id}/run`, {});
}

export async function retryProduction(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/retry`, payload || {});
}

export async function getProductionManualScriptPrompt(id) {
  return api.get(`/api/production-studio/productions/${id}/manual-script/prompt`);
}

export async function submitProductionManualScript(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/manual-script`, payload || {});
}

export async function approveProductionScript(id) {
  return api.post(`/api/production-studio/productions/${id}/script/approve`, {});
}

export async function getProductionEditorialReview(id) {
  return api.get(`/api/production-studio/productions/${id}/editorial-review`);
}

export async function approveProductionEditorialReview(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/editorial-review/approve`, payload || {});
}

export async function rejectProductionEditorialReview(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/editorial-review/reject`, payload || {});
}

export async function retryProductionEditorialFromStage(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/editorial-review/retry`, payload || {});
}

export async function getProductionNarrativeExcellence(id, reportId) {
  const qs = new URLSearchParams();
  if (reportId != null) qs.set('reportId', String(reportId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions/${id}/narrative-excellence${suffix}`);
}

export async function listProductionNarrativeExcellenceReports(id) {
  return api.get(`/api/production-studio/productions/${id}/narrative-excellence/reports`);
}

export async function runProductionNarrativeExcellence(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/narrative-excellence/run`, payload || {});
}

export async function resolveProductionNarrativeExcellenceSuggestion(id, suggestionId, payload) {
  return api.put(`/api/production-studio/productions/${id}/narrative-excellence/suggestions/${suggestionId}`, payload || {});
}

export async function getProductionCognitiveEngagement(id, reportId) {
  const qs = new URLSearchParams();
  if (reportId != null) qs.set('reportId', String(reportId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions/${id}/cognitive-engagement${suffix}`);
}

export async function listProductionCognitiveEngagementReports(id) {
  return api.get(`/api/production-studio/productions/${id}/cognitive-engagement/reports`);
}

export async function runProductionCognitiveEngagement(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/cognitive-engagement/run`, payload || {});
}

export async function resolveProductionCognitiveEngagementSuggestion(id, suggestionId, payload) {
  return api.put(`/api/production-studio/productions/${id}/cognitive-engagement/suggestions/${suggestionId}`, payload || {});
}

export async function getProductionVisualExcellence(id, reportId) {
  const qs = new URLSearchParams();
  if (reportId != null) qs.set('reportId', String(reportId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions/${id}/visual-excellence${suffix}`);
}

export async function listProductionVisualExcellenceReports(id) {
  return api.get(`/api/production-studio/productions/${id}/visual-excellence/reports`);
}

export async function runProductionVisualExcellence(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/visual-excellence/run`, payload || {});
}

export async function resolveProductionVisualExcellenceSuggestion(id, suggestionId, payload) {
  return api.put(`/api/production-studio/productions/${id}/visual-excellence/suggestions/${suggestionId}`, payload || {});
}

export async function getProductionNarrationExcellence(id, reportId) {
  const qs = new URLSearchParams();
  if (reportId != null) qs.set('reportId', String(reportId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions/${id}/narration-excellence${suffix}`);
}

export async function listProductionNarrationExcellenceReports(id) {
  return api.get(`/api/production-studio/productions/${id}/narration-excellence/reports`);
}

export async function runProductionNarrationExcellence(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/narration-excellence/run`, payload || {});
}

export async function resolveProductionNarrationExcellenceSuggestion(id, suggestionId, payload) {
  return api.put(`/api/production-studio/productions/${id}/narration-excellence/suggestions/${suggestionId}`, payload || {});
}

export async function getProductionGlobalDocumentaryInspection(id, reportId) {
  const qs = new URLSearchParams();
  if (reportId != null) qs.set('reportId', String(reportId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions/${id}/global-documentary-inspector${suffix}`);
}

export async function listProductionGlobalDocumentaryInspections(id) {
  return api.get(`/api/production-studio/productions/${id}/global-documentary-inspector/reports`);
}

export async function runProductionGlobalDocumentaryInspection(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/global-documentary-inspector/run`, payload || {});
}

export async function resolveProductionGlobalDocumentaryInspectionSuggestion(id, suggestionId, payload) {
  return api.put(`/api/production-studio/productions/${id}/global-documentary-inspector/suggestions/${suggestionId}`, payload || {});
}

export async function getProductionDocumentaryIntelligence(id, reportId) {
  const qs = new URLSearchParams();
  if (reportId != null) qs.set('reportId', String(reportId));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/production-studio/productions/${id}/documentary-intelligence${suffix}`);
}

export async function listProductionDocumentaryIntelligenceReports(id) {
  return api.get(`/api/production-studio/productions/${id}/documentary-intelligence/reports`);
}

export async function runProductionDocumentaryIntelligence(id, payload) {
  return api.post(`/api/production-studio/productions/${id}/documentary-intelligence/run`, payload || {});
}

export async function resolveProductionDocumentaryIntelligenceSuggestion(id, suggestionId, payload) {
  return api.put(`/api/production-studio/productions/${id}/documentary-intelligence/suggestions/${suggestionId}`, payload || {});
}

const _PRC = (id) => `/api/production-studio/productions/${id}/review`;

export async function getProductionReviewSummary(id) {
  return api.get(`${_PRC(id)}/summary`);
}

export async function getProductionReviewScenes(id) {
  return api.get(`${_PRC(id)}/scenes`);
}

export async function setProductionReviewSceneStatus(id, sceneId, sceneStatus, patch) {
  return api.patch(`${_PRC(id)}/scenes/${sceneId}/status`, { sceneStatus, patch });
}

export async function approveProductionReviewScene(id, sceneId) {
  return api.post(`${_PRC(id)}/scenes/${sceneId}/approve`, {});
}

export async function rejectProductionReviewScene(id, sceneId) {
  return api.post(`${_PRC(id)}/scenes/${sceneId}/reject`, {});
}

export async function approveAllProductionReviewScenes(id) {
  return api.post(`${_PRC(id)}/approve-all`, {});
}

export async function setProductionReviewScenePrompt(id, sceneId, prompt) {
  return api.patch(`${_PRC(id)}/scenes/${sceneId}/prompt`, { prompt });
}

export async function regenerateProductionReviewScene(id, sceneId, payload) {
  return api.post(`${_PRC(id)}/scenes/${sceneId}/regenerate`, payload || {});
}

export async function regenerateProductionReviewRejected(id) {
  return api.post(`${_PRC(id)}/regenerate-rejected`, {});
}

export async function uploadProductionReviewSceneImage(id, sceneId, file, onProgress) {
  const form = new FormData();
  form.append('file', file);
  return api.upload(`${_PRC(id)}/scenes/${sceneId}/upload`, form, { onProgress });
}

export async function selectProductionReviewSceneVersion(id, sceneId, versionId) {
  return api.patch(`${_PRC(id)}/scenes/${sceneId}/versions/${versionId}/select`, {});
}

export async function getProductionDashboardSnapshot() {
  return api.get('/api/production-dashboard/snapshot');
}

export async function listVideoPreviews(videoId, limit) {
  const qs = new URLSearchParams();
  if (limit != null) qs.set('limit', String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api.get(`/api/video-preview/videos/${videoId}/previews${suffix}`);
}

export async function prepareVideoPreview(videoId) {
  return api.post(`/api/video-preview/videos/${videoId}/prepare`, {});
}

export async function getVideoPreview(previewId) {
  return api.get(`/api/video-preview/previews/${previewId}`);
}

export async function mintVideoPreviewPublic(previewId, ttlMinutes) {
  return api.post(`/api/video-preview/previews/${previewId}/mint-public`, { ttlMinutes: ttlMinutes != null ? Number(ttlMinutes) : 120 });
}

export async function retryVideoPreview(previewId) {
  return api.post(`/api/video-preview/previews/${previewId}/retry`, {});
}
